import React from 'react';

export default function AdminOrgs() {
  const [orgs, setOrgs] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [selected, setSelected] = React.useState(null);
  const [monthlyUsageDetails, setMonthlyUsageDetails] = React.useState(null);
  const [monthlyUsageLoading, setMonthlyUsageLoading] = React.useState(false);
  const [aggLoading, setAggLoading] = React.useState(false);
  const [aggError, setAggError] = React.useState('');
  const [year, setYear] = React.useState(() => new Date().getUTCFullYear());
  const [month, setMonth] = React.useState(() => String(new Date().getUTCMonth() + 1).padStart(2, '0'));
  const [monthlyAgg, setMonthlyAgg] = React.useState(null);
  const [yearlyAgg, setYearlyAgg] = React.useState(null);
  const [summaryMonthly, setSummaryMonthly] = React.useState(null);
  const [summaryYearly, setSummaryYearly] = React.useState(null);
  const [runMonthBusy, setRunMonthBusy] = React.useState(false);
  const [runDayBusy, setRunDayBusy] = React.useState(false);
  const [orgPickerOpen, setOrgPickerOpen] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState('overview');
  const [orgUsageData, setOrgUsageData] = React.useState([]);
  const [orgUsageLoading, setOrgUsageLoading] = React.useState(false);
  const [selectedYear, setSelectedYear] = React.useState(() => new Date().getUTCFullYear());
  const [selectedMonth, setSelectedMonth] = React.useState(null); // null means yearly view
  const [viewMode, setViewMode] = React.useState('yearly'); // 'yearly' or 'monthly'
  const usageLogsRef = React.useRef(null);

  // Simple data sanitization function
  const sanitizeOrg = (org, currentViewMode = viewMode, currentSelectedMonth = selectedMonth) => {
    if (!org || typeof org !== 'object') {
      return {
        orgId: '',
        name: 'Unknown',
        isActive: false,
        totalInvocations: 0,
        totalReads: 0,
        totalWrites: 0,
        monthlyInvocations: 0,
        yearlyInvocations: 0
      };
    }

    // Determine if org is active based on current view mode
    const yearlyInvocations = Number(org.yearlyUsage?.invocations) || 0;
    const monthlyInvocations = Number(org.monthlyUsage?.invocations) || 0;
    
    // For monthly view with selected month, use monthly data; otherwise use yearly data
    const isActive = (currentViewMode === 'monthly' && currentSelectedMonth) 
      ? monthlyInvocations > 0 
      : yearlyInvocations > 0;

    const sanitized = {
      orgId: String(org.orgId || ''),
      name: String(org.name || org.orgName || 'Unknown'),
      isActive: isActive, // Determine based on usage data
      totalInvocations: yearlyInvocations, // Map from yearly usage data
      totalReads: Number(org.yearlyUsage?.firestoreReads) || 0, // Map from yearly usage data
      totalWrites: Number(org.yearlyUsage?.firestoreWrites) || 0, // Map from yearly usage data
      monthlyInvocations: monthlyInvocations,
      yearlyInvocations: yearlyInvocations,
      // Preserve the original monthly and yearly usage objects for detailed access
      monthlyUsage: org.monthlyUsage,
      yearlyUsage: org.yearlyUsage
    };

    // Debug logging for first org
    if (org === orgUsageData[0]) {
      console.log('=== DEBUG: Sanitizing first org ===');
      console.log('Original org:', org);
      console.log('Sanitized org:', sanitized);
    }

    return sanitized;
  };

  const loadOrgs = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await fetch(`${process.env.REACT_APP_API_BASE}/admin/orgs`, {
        headers: { 'Authorization': 'Bearer ' + (localStorage.getItem('authToken')||'') },
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setOrgs(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.message || 'Failed to load orgs');
    } finally {
      setLoading(false);
    }
  };

  const loadOrgUsageData = async () => {
    if (!Array.isArray(orgs) || orgs.length === 0) return;
    
    console.log('=== DEBUG: loadOrgUsageData called ===');
    console.log('View mode:', viewMode);
    console.log('Selected month:', selectedMonth);
    console.log('Selected year:', selectedYear);
    console.log('Orgs count:', orgs.length);
    
    setOrgUsageLoading(true);
    try {
      const usagePromises = orgs.map(async (org) => {
        try {
          const headers = { 'Authorization': 'Bearer ' + (localStorage.getItem('authToken')||'') };
          
          // Always fetch yearly data with scope=year
          const qsYear = new URLSearchParams({ 
            orgId: org.orgId, 
            year: String(selectedYear),
            scope: 'year'
          });
          const yearRes = await fetch(`${process.env.REACT_APP_API_BASE}/admin/usage/aggregates?${qsYear}`, { headers });
          const yearlyData = yearRes.ok ? await yearRes.json() : null;
          
          // Debug logging for first org
          if (org === orgs[0]) {
            console.log('=== DEBUG: Yearly data for first org ===');
            console.log('Org ID:', org.orgId);
            console.log('Yearly data:', yearlyData);
          }
          
          // Only fetch monthly data if a specific month is selected
          let monthlyData = null;
          if (viewMode === 'monthly' && selectedMonth) {
            console.log(`=== DEBUG: Fetching monthly data for org ${org.orgId}, month ${selectedMonth} ===`);
            const qsMonth = new URLSearchParams({ 
              orgId: org.orgId, 
              year: String(selectedYear), 
              month: String(selectedMonth)
              // No scope parameter for monthly (defaults to monthly)
            });
            const monthRes = await fetch(`${process.env.REACT_APP_API_BASE}/admin/usage/aggregates?${qsMonth}`, { headers });
            monthlyData = monthRes.ok ? await monthRes.json() : null;
            console.log(`=== DEBUG: Monthly data for org ${org.orgId}:`, monthlyData);
          }
          
          return {
            ...org,
            monthlyUsage: monthlyData,
            yearlyUsage: yearlyData
          };
        } catch (error) {
          console.error(`Failed to load usage for org ${org.orgId}:`, error);
          return {
            ...org,
            monthlyUsage: null,
            yearlyUsage: null
          };
        }
      });
      
      const results = await Promise.all(usagePromises);
      setOrgUsageData(results);
    } catch (error) {
      console.error('Failed to load org usage data:', error);
    } finally {
      setOrgUsageLoading(false);
    }
  };

  const scrollToUsageLogs = () => {
    if (usageLogsRef.current) {
      usageLogsRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  };

  const fetchMonthlyUsageDetails = async (orgId) => {
    if (!orgId || viewMode !== 'monthly' || !selectedMonth) {
      setMonthlyUsageDetails(null);
      return;
    }
    
    console.log('=== DEBUG: Fetching monthly usage details for org:', orgId);
    console.log('Month:', selectedMonth, 'Year:', selectedYear);
    
    setMonthlyUsageLoading(true);
    setError('');
    
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        throw new Error('No authentication token found');
      }

      const qs = new URLSearchParams({ 
        orgId, 
        month: selectedMonth, 
        year: selectedYear 
      });
      const apiUrl = `${process.env.REACT_APP_API_BASE}/admin/usage/aggregates?${qs}`;
      console.log('Monthly details API URL:', apiUrl);
      
      const res = await fetch(apiUrl, {
        headers: { 
          'Authorization': 'Bearer ' + authToken,
          'Content-Type': 'application/json'
        },
      });
      
      console.log('Monthly details response status:', res.status);
      
      if (!res.ok) {
        const errorText = await res.text();
        console.error('Monthly details API Error Response:', errorText);
        throw new Error(`Monthly details API Error (${res.status}): ${errorText}`);
      }
      
      const data = await res.json();
      console.log('Monthly details data received:', data);
      
      if (!data || (typeof data === 'object' && Object.keys(data).length === 0)) {
        console.warn('Empty monthly details data received');
        setMonthlyUsageDetails(null);
        return;
      }
      
      // Transform monthly data to include day-wise breakdown
      if (data && data.days) {
        data.dayWiseData = Object.entries(data.days).map(([date, dayData]) => ({
          date,
          invocations: dayData.invocations || 0,
          firestoreReads: dayData.firestoreReads || 0,
          firestoreWrites: dayData.firestoreWrites || 0,
          fcmSend: dayData.fcmSend || 0,
          avgDurationMs: dayData.avgDurationMs || 0,
          byAction: dayData.byAction || {}
        })).sort((a, b) => new Date(a.date) - new Date(b.date));
      }
      
      setMonthlyUsageDetails(data);
      
    } catch (e) {
      console.error('Error fetching monthly usage details:', e);
      setError(e?.message || 'Failed to fetch monthly usage details');
      setMonthlyUsageDetails(null);
    } finally {
      setMonthlyUsageLoading(false);
    }
  };

  const fetchAggregatesForOrg = async (orgId) => {
    if (!orgId) return;
    
    setAggLoading(true);
    setAggError('');
    try {
      // Use the current view mode to determine what data to fetch
      if (viewMode === 'monthly' && selectedMonth) {
        // Fetch monthly data
        const qs = new URLSearchParams({ 
          orgId, 
          year: String(selectedYear), 
          month: String(selectedMonth)
        });
        const res = await fetch(`${process.env.REACT_APP_API_BASE}/admin/usage/aggregates?${qs}`, {
          headers: { 'Authorization': 'Bearer ' + (localStorage.getItem('authToken')||'') },
        });
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        setMonthlyAgg(data);
      } else {
        // Fetch yearly data
        const qs = new URLSearchParams({ 
          orgId, 
          year: String(selectedYear),
          scope: 'year'
        });
        const res = await fetch(`${process.env.REACT_APP_API_BASE}/admin/usage/aggregates?${qs}`, {
          headers: { 'Authorization': 'Bearer ' + (localStorage.getItem('authToken')||'') },
        });
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        setMonthlyAgg(data); // Using monthlyAgg state for display
      }
    } catch (e) {
      setAggError(e?.message || 'Failed to fetch aggregates');
    } finally {
      setAggLoading(false);
    }
  };

  const fetchSummaryAggregates = async () => {
    setAggLoading(true);
    setAggError('');
    try {
      // Only fetch yearly summary data initially
      const qsYear = new URLSearchParams({ year: String(selectedYear) });
      
      const yearRes = await fetch(`${process.env.REACT_APP_API_BASE}/admin/usage/aggregates/summary?${qsYear}`, {
        headers: { 'Authorization': 'Bearer ' + (localStorage.getItem('authToken')||'') },
      });
      
      if (yearRes.ok) {
        const yearData = await yearRes.json();
        setSummaryYearly(yearData);
      }
      
      // Only fetch monthly summary if in monthly mode and month is selected
      if (viewMode === 'monthly' && selectedMonth) {
        const qsMonth = new URLSearchParams({ 
          year: String(selectedYear), 
          month: String(selectedMonth) 
        });
        
        const monthRes = await fetch(`${process.env.REACT_APP_API_BASE}/admin/usage/aggregates/summary?${qsMonth}`, {
          headers: { 'Authorization': 'Bearer ' + (localStorage.getItem('authToken')||'') },
        });
        
        if (monthRes.ok) {
          const monthData = await monthRes.json();
          setSummaryMonthly(monthData);
        }
      }
    } catch (e) {
      setAggError(e?.message || 'Failed to fetch summary aggregates');
    } finally {
      setAggLoading(false);
    }
  };

  React.useEffect(() => { loadOrgs(); }, []);
  React.useEffect(() => { fetchSummaryAggregates(); }, [selectedYear, viewMode, selectedMonth]);
  React.useEffect(() => { loadOrgUsageData(); }, [orgs, selectedYear, selectedMonth, viewMode]);
  React.useEffect(() => {
    if (selected && selected.orgId) {
      fetchAggregatesForOrg(selected.orgId);
      
      // Automatically fetch monthly usage details when in monthly view
      if (viewMode === 'monthly' && selectedMonth) {
        fetchMonthlyUsageDetails(selected.orgId);
      } else {
        setMonthlyUsageDetails(null);
      }
    }
  }, [selected, selectedYear, selectedMonth, viewMode]);

  // Force data refresh when switching to monthly view and selecting a month
  React.useEffect(() => {
    if (viewMode === 'monthly' && selectedMonth && orgs.length > 0) {
      console.log('=== DEBUG: Monthly view with month selected, refreshing data ===');
      loadOrgUsageData();
    }
  }, [viewMode, selectedMonth, orgs.length]);


  // Simple statistics calculation
  const stats = React.useMemo(() => {
    const sanitizedOrgs = orgUsageData.map(org => sanitizeOrg(org, viewMode, selectedMonth));
    
    const activeOrgs = sanitizedOrgs.filter(org => org.isActive);
    const inactiveOrgs = sanitizedOrgs.filter(org => !org.isActive);
    
    console.log('=== DEBUG: Stats calculation ===');
    console.log('View mode:', viewMode);
    console.log('Selected month:', selectedMonth);
    console.log('Total orgs:', sanitizedOrgs.length);
    console.log('Active orgs:', activeOrgs.length);
    console.log('Inactive orgs:', inactiveOrgs.length);
    console.log('Active orgs details:', activeOrgs.map(org => ({
      name: org.name,
      isActive: org.isActive,
      totalInvocations: org.totalInvocations,
      yearlyInvocations: org.yearlyInvocations,
      monthlyInvocations: org.monthlyInvocations
    })));
    
    return {
      totalOrgs: sanitizedOrgs.length,
      activeOrgs: activeOrgs.length,
      inactiveOrgs: inactiveOrgs.length,
      totalInvocations: sanitizedOrgs.reduce((sum, org) => sum + org.totalInvocations, 0),
      totalReads: sanitizedOrgs.reduce((sum, org) => sum + org.totalReads, 0),
      totalWrites: sanitizedOrgs.reduce((sum, org) => sum + org.totalWrites, 0)
    };
  }, [orgUsageData, viewMode, selectedMonth]);

  // Simple filtered orgs
  const filteredOrgs = React.useMemo(() => {
    const sanitizedOrgs = orgUsageData.map(org => sanitizeOrg(org, viewMode, selectedMonth));
    
    switch (activeTab) {
      case 'active-orgs':
        return sanitizedOrgs.filter(org => org.isActive);
      case 'total-usage':
        return [...sanitizedOrgs].sort((a, b) => b.totalInvocations - a.totalInvocations);
      case 'monthly-usage':
        if (viewMode === 'monthly' && selectedMonth) {
          return [...sanitizedOrgs].sort((a, b) => b.monthlyInvocations - a.monthlyInvocations);
        } else {
          return []; // Show empty if no month selected
        }
      case 'yearly-usage':
        return [...sanitizedOrgs].sort((a, b) => b.yearlyInvocations - a.yearlyInvocations);
      default:
        return sanitizedOrgs;
    }
  }, [orgUsageData, activeTab, viewMode, selectedMonth]);

  const tabs = [
    { id: 'overview', label: 'Overview', count: stats.totalOrgs },
    { id: 'active-orgs', label: 'Active Orgs', count: stats.activeOrgs },
    { id: 'total-usage', label: 'Total Usage', count: stats.totalOrgs },
    { id: 'monthly-usage', label: viewMode === 'monthly' && selectedMonth 
      ? `Monthly Usage (${new Date(2000, parseInt(selectedMonth) - 1).toLocaleString('default', { month: 'short' })})`
      : 'Monthly Usage', count: stats.totalOrgs },
    { id: 'yearly-usage', label: `Yearly Usage (${selectedYear})`, count: stats.totalOrgs }
  ];

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="text-lg font-bold text-green-700">Admin – Organization Usage Insights</div>
        <button
          className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50"
          onClick={() => { loadOrgs(); fetchSummaryAggregates(); loadOrgUsageData(); }}
          disabled={loading || orgUsageLoading}
        >
          Refresh
        </button>
      </div>

      {/* Year/Month Selector */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Year:</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="px-3 py-1 border border-gray-300 rounded text-sm"
            >
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">View:</label>
            <div className="flex border border-gray-300 rounded">
              <button
                className={`px-3 py-1 text-sm ${viewMode === 'yearly' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                onClick={() => {
                  setViewMode('yearly');
                  setSelectedMonth(null);
                }}
              >
                Yearly
              </button>
              <button
                className={`px-3 py-1 text-sm border-l border-gray-300 ${viewMode === 'monthly' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                onClick={() => setViewMode('monthly')}
              >
                Monthly
              </button>
            </div>
          </div>
          
          {viewMode === 'monthly' && (
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Month:</label>
              <select
                value={selectedMonth || ''}
                onChange={(e) => {
                  const newMonth = e.target.value || null;
                  setSelectedMonth(newMonth);
                  console.log('=== DEBUG: Month changed to:', newMonth);
                }}
                className="px-3 py-1 border border-gray-300 rounded text-sm"
              >
                <option value="">Select Month</option>
                {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0')).map(month => (
                  <option key={month} value={month}>
                    {new Date(2000, parseInt(month) - 1).toLocaleString('default', { month: 'long' })} ({month})
                  </option>
                ))}
              </select>
          </div>
        )}
          
          <div className="flex items-center gap-4">
            <div className="text-sm text-gray-600">
              {viewMode === 'yearly' 
                ? `Showing yearly data for ${selectedYear}`
                : selectedMonth 
                  ? `Showing monthly data for ${new Date(2000, parseInt(selectedMonth) - 1).toLocaleString('default', { month: 'long' })} ${selectedYear}`
                  : `Select a month to view monthly data for ${selectedYear}`
              }
            </div>
            {viewMode === 'monthly' && selectedMonth && (
              <button
                className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                onClick={() => {
                  console.log('=== DEBUG: Manual refresh for monthly data ===');
                  loadOrgUsageData();
                }}
                disabled={orgUsageLoading}
              >
                {orgUsageLoading ? 'Loading...' : 'Refresh Monthly Data'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Selected Organization Details - Moved to Top */}
      {selected && selected.orgId && (
        <div className="bg-white border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-blue-800">
              📊 Selected Organization: {selected.name}
            </h3>
            <button
              className="px-3 py-1 bg-gray-500 text-white rounded text-sm"
              onClick={() => setSelected(null)}
            >
              Clear Selection
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-medium text-gray-700 mb-2">Year/Month for Aggregates</h4>
              <div className="flex gap-2">
                <input
                  type="number"
                  value={year}
                  onChange={(e) => setYear(Number(e.target.value))}
                  className="px-3 py-1 border border-gray-300 rounded text-sm w-20"
                />
                <input
                  type="number"
                  value={month}
                  onChange={(e) => setMonth(String(e.target.value).padStart(2, '0'))}
                  className="px-3 py-1 border border-gray-300 rounded text-sm w-16"
                  min="1"
                  max="12"
                />
                <button
                  className="px-3 py-1 bg-green-600 text-white rounded text-sm"
                  onClick={() => fetchAggregatesForOrg(selected.orgId)}
                  disabled={aggLoading}
                >
                  {aggLoading ? 'Loading...' : 'Fetch Aggregates'}
                </button>
              </div>
            </div>
            
            {viewMode === 'monthly' && selectedMonth && (
              <div>
                <h4 className="font-medium text-gray-700 mb-2">Monthly Details</h4>
                <div className="text-sm text-gray-600">
                  Showing day-wise data for {new Date(2000, parseInt(selectedMonth) - 1).toLocaleString('default', { month: 'long' })} {selectedYear}
                </div>
            </div>
          )}
        </div>

          {/* Monthly Usage Details - Only shown in monthly view */}
          {viewMode === 'monthly' && selectedMonth && (
            <>
              {monthlyUsageLoading && (
                <div className="mt-4">
                  <h4 className="font-medium text-gray-700 mb-2">Monthly Usage Details</h4>
                  <div className="bg-gray-50 rounded p-3 text-center">
                    <div className="text-sm text-gray-600">Loading monthly details...</div>
                  </div>
                </div>
              )}

              {monthlyUsageDetails && !monthlyUsageLoading && (
                <div className="mt-4">
                  <h4 className="font-medium text-gray-700 mb-2">Monthly Usage Details</h4>
                  <div className="bg-gray-50 rounded p-3 max-h-96 overflow-auto">
                    <div className="space-y-3">
                      {/* Summary Stats */}
                      <div className="bg-white rounded p-3 border">
                        <div className="text-sm font-medium text-gray-700 mb-2">Summary</div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className="font-medium">Invocations:</span> {monthlyUsageDetails.invocations || 0}
                          </div>
                          <div>
                            <span className="font-medium">Reads:</span> {monthlyUsageDetails.firestoreReads || 0}
                          </div>
                          <div>
                            <span className="font-medium">Writes:</span> {monthlyUsageDetails.firestoreWrites || 0}
                          </div>
        <div>
                            <span className="font-medium">FCM:</span> {monthlyUsageDetails.fcmSend || 0}
                          </div>
                          <div className="col-span-2">
                            <span className="font-medium">Avg Duration:</span> {monthlyUsageDetails.avgDurationMs || 0}ms
                          </div>
                        </div>
                      </div>

                      {/* Day-wise Data */}
                      {monthlyUsageDetails.dayWiseData && monthlyUsageDetails.dayWiseData.length > 0 && (
                        <div className="bg-white rounded p-3 border">
                          <div className="text-sm font-medium text-gray-700 mb-2">
                            Day-wise Breakdown ({monthlyUsageDetails.dayWiseData.length} days)
                          </div>
                          <div className="space-y-2 max-h-48 overflow-auto">
                            {monthlyUsageDetails.dayWiseData.map((day, index) => (
                              <div key={day.date || index} className="bg-gray-50 rounded p-2 text-xs">
                                <div className="font-medium text-blue-600 mb-1">
                                  {new Date(day.date).toLocaleDateString('en-US', { 
                                    month: 'short', 
                                    day: 'numeric',
                                    weekday: 'short'
                                  })}
                                </div>
                                <div className="grid grid-cols-2 gap-1 text-gray-600">
                                  <div>Invocations: {day.invocations}</div>
                                  <div>Reads: {day.firestoreReads}</div>
                                  <div>Writes: {day.firestoreWrites}</div>
                                  <div>FCM: {day.fcmSend}</div>
                                  <div className="col-span-2">Duration: {day.avgDurationMs}ms</div>
                                </div>
                                {day.byAction && Object.keys(day.byAction).length > 0 && (
                                  <div className="mt-1 pt-1 border-t border-gray-200">
                                    <div className="text-xs text-gray-500 mb-1">Actions:</div>
                                    <div className="flex flex-wrap gap-1">
                                      {Object.entries(day.byAction).slice(0, 3).map(([action, data]) => (
                                        <span key={action} className="bg-blue-100 text-blue-700 px-1 py-0.5 rounded text-xs">
                                          {action}({data.count})
                                        </span>
                                      ))}
                                      {Object.keys(day.byAction).length > 3 && (
                                        <span className="text-gray-400 text-xs">
                                          +{Object.keys(day.byAction).length - 3} more
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                    </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {monthlyAgg && (
            <div className="mt-4">
              <h4 className="font-medium text-gray-700 mb-2">
                {viewMode === 'monthly' && selectedMonth 
                  ? `Monthly Aggregates (${new Date(2000, parseInt(selectedMonth) - 1).toLocaleString('default', { month: 'long' })} ${selectedYear})`
                  : `Yearly Aggregates (${selectedYear})`
                }
              </h4>
              <div className="bg-gray-50 rounded p-3">
                <div className="text-sm text-gray-600">
                  Invocations: {monthlyAgg.invocations || 0} | 
                  Reads: {monthlyAgg.firestoreReads || 0} | 
                  Writes: {monthlyAgg.firestoreWrites || 0}
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
              <div className="font-medium">Error:</div>
              <div>{error}</div>
            </div>
          )}
        </div>
      )}

      {/* Dashboard Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-green-100 rounded-lg p-4">
          <div className="text-2xl font-bold text-green-700">{stats.totalOrgs}</div>
          <div className="text-sm text-gray-600">Total Organizations</div>
        </div>
        <div className="bg-white border border-green-100 rounded-lg p-4">
          <div className="text-2xl font-bold text-blue-700">{stats.activeOrgs}</div>
          <div className="text-sm text-gray-600">
            Active Organizations {viewMode === 'monthly' && selectedMonth 
              ? `(${new Date(2000, parseInt(selectedMonth) - 1).toLocaleString('default', { month: 'short' })} ${selectedYear})`
              : `(${selectedYear})`
            }
          </div>
        </div>
        <div className="bg-white border border-green-100 rounded-lg p-4">
          <div className="text-2xl font-bold text-purple-700">{stats.totalInvocations.toLocaleString()}</div>
          <div className="text-sm text-gray-600">
            Total Invocations {viewMode === 'yearly' 
              ? `(${selectedYear})`
              : selectedMonth 
                ? `(${new Date(2000, parseInt(selectedMonth) - 1).toLocaleString('default', { month: 'short' })} ${selectedYear})`
                : `(${selectedYear})`
            }
          </div>
        </div>
        <div className="bg-white border border-green-100 rounded-lg p-4">
          <div className="text-2xl font-bold text-orange-700">{stats.inactiveOrgs}</div>
          <div className="text-sm text-gray-600">Inactive Organizations</div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              activeTab === tab.id
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
              </div>

      {/* Content */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">
            {activeTab === 'overview' && 'Organization Overview'}
            {activeTab === 'active-orgs' && 'Active Organizations'}
            {activeTab === 'total-usage' && 'Total Usage Ranking'}
            {activeTab === 'monthly-usage' && (viewMode === 'monthly' && selectedMonth 
              ? `Monthly Usage (${new Date(2000, parseInt(selectedMonth) - 1).toLocaleString('default', { month: 'long' })} ${selectedYear})`
              : 'Monthly Usage - Select a month to view data'
            )}
            {activeTab === 'yearly-usage' && `Yearly Usage (${selectedYear})`}
          </h3>
          <p className="text-sm text-gray-600">
            {filteredOrgs.length} organization{filteredOrgs.length !== 1 ? 's' : ''} found
          </p>
              </div>

        {orgUsageLoading ? (
          <div className="text-center py-8">
            <div className="text-sm text-gray-500">Loading organization data...</div>
          </div>
        ) : (
          <div className="grid gap-3">
            {filteredOrgs.map((org, index) => (
              <div
                key={`${org.orgId}-${index}`}
                className={`border rounded-lg p-4 ${
                  org.isActive ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                }`}
              >
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="font-medium text-gray-900 text-base">
                        {org.name}
        </div>
                      {org.isActive ? (
                        <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                          Active
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                          Inactive
                        </span>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-gray-600">
                      <div>
                        <span className="font-medium">ID:</span> {org.orgId}
                      </div>
                      {viewMode === 'monthly' && selectedMonth ? (
                        <>
                          <div>
                            <span className="font-medium">Monthly Invocations:</span> {org.monthlyInvocations.toLocaleString()}
                          </div>
                          <div>
                            <span className="font-medium">Monthly Reads:</span> {Number(org.monthlyUsage?.firestoreReads) || 0}
                          </div>
                          <div>
                            <span className="font-medium">Monthly Writes:</span> {Number(org.monthlyUsage?.firestoreWrites) || 0}
                          </div>
                          <div>
                            <span className="font-medium">Yearly Invocations:</span> {org.yearlyInvocations.toLocaleString()}
                          </div>
                        </>
                      ) : (
                        <>
                          <div>
                            <span className="font-medium">Total Invocations:</span> {org.totalInvocations.toLocaleString()}
                          </div>
                          <div>
                            <span className="font-medium">Total Reads:</span> {org.totalReads.toLocaleString()}
                          </div>
                          <div>
                            <span className="font-medium">Total Writes:</span> {org.totalWrites.toLocaleString()}
                          </div>
        <div>
                            <span className="font-medium">Yearly Invocations:</span> {org.yearlyInvocations.toLocaleString()}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-2">
                    <button 
                      className="px-3 py-1 rounded bg-green-700 text-white text-xs" 
                      onClick={() => { 
                        setSelected({
                          orgId: org.orgId,
                          name: org.name
                        }); 
                        setOrgPickerOpen(false); 
                      }}
                    >
                      Select for Details
                    </button>
                  </div>
                </div>
              </div>
            ))}
            
            {filteredOrgs.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <div className="text-lg mb-2">
                  {activeTab === 'monthly-usage' && viewMode === 'monthly' && !selectedMonth 
                    ? 'Select a month to view monthly data'
                    : 'No organizations found'
                  }
                </div>
                <div className="text-sm">
                  {activeTab === 'active-orgs' ? 'No active organizations' : 
                   activeTab === 'monthly-usage' && viewMode === 'monthly' && !selectedMonth 
                     ? 'Choose a month from the selector above to view monthly usage data'
                     : 'No organizations match the current filter'
                  }
                </div>
              </div>
            )}
          </div>
        )}
        </div>

    </div>
  );
}